import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, Newspaper, ChevronDown, ArrowRight, X, Check } from "lucide-react";
import api, { imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useIsMobile, ExpandPill } from "@/components/MobileCollapse";
import { useLang } from "@/context/LanguageContext";

const todayStr = () => new Date().toISOString().slice(0, 10);

// Stagione agonistica: da luglio a giugno dell'anno dopo
function seasonWindow() {
  const now = new Date();
  const y = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return [`${y}-07-01`, `${y + 1}-06-30`];
}

// Circuito: WC Coppa del Mondo, WCH Mondiali, PAR Paralimpiadi
const CIRCUIT_STYLE = {
  wc: { label: "WC", cls: "border-sky-400/60 bg-sky-400/10 text-sky-300" },
  wch: { label: "WCH", cls: "border-amber-400/70 bg-amber-400/10 text-amber-400" },
  paralimpiadi: { label: "PAR", cls: "border-emerald-400/70 bg-emerald-400/10 text-emerald-400" },
};

// Discipline: tecniche (SL, GS) indaco, veloci (DH, SG) arancio, combinata fucsia
const DISCIPLINE_STYLE = {
  "slalom speciale": "border-indigo-400/50 text-indigo-300",
  "slalom gigante": "border-indigo-400/50 text-indigo-300",
  "discesa libera": "border-orange-400/50 text-orange-300",
  "super-g": "border-orange-400/50 text-orange-300",
  "super combinata": "border-fuchsia-400/50 text-fuchsia-300",
};
const disciplineCls = (d) => DISCIPLINE_STYLE[d.toLowerCase()] || "border-slate-600 text-slate-400";

// Codice nazione nel luogo "(SUI)" → bandierina via flagcdn (immagini: visibili anche dove le emoji bandiera non esistono)
const COUNTRY_FLAGS = {
  ITA: "it", AUT: "at", SUI: "ch", GER: "de", SLO: "si", FRA: "fr",
  USA: "us", CAN: "ca", JPN: "jp", KOR: "kr", NOR: "no", SWE: "se", FIN: "fi",
  CZE: "cz", SVK: "sk", POL: "pl", ESP: "es", AND: "ad", GBR: "gb", CRO: "hr", BUL: "bg",
};
const countryOf = (location) => {
  const m = (location || "").match(/\(([A-Z]{3})\)\s*$/);
  return m ? m[1] : null;
};
const mapsUrl = (location) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((location || "").replace(/\s*\([A-Z]{3}\)\s*$/, ""))}`;

function daysUntil(dateStr) {
  const today = new Date(todayStr() + "T00:00:00");
  const target = new Date(dateStr + "T00:00:00");
  return Math.round((target - today) / 86400000);
}

function CountdownChip({ days }) {
  const { t } = useLang();
  const label = days === 0 ? t("news.today") : days === 1 ? t("news.tomorrow") : t("news.inDays", { n: days });
  return (
    <span
      data-testid="event-countdown-chip"
      className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#090D14] shadow-[0_0_18px_rgba(245,158,11,0.4)]"
    >
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#090D14]" />
      {label}
    </span>
  );
}

function EventRow({ event, isNext, locale, index }) {
  const { t } = useLang();
  const country = countryOf(event.location);
  const flagCode = country ? COUNTRY_FLAGS[country] : null;
  const d = new Date(event.date + "T00:00:00");
  const day = d.toLocaleDateString(locale, { day: "2-digit" });
  const month = d.toLocaleDateString(locale, { month: "short" }).replace(".", "").toUpperCase();
  const range = event.date_end
    ? `${d.toLocaleDateString(locale, { day: "numeric", month: "short" })} – ${new Date(event.date_end + "T00:00:00").toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}`
    : d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });

  return (
    <motion.div
      data-testid={`event-row-${event.id}`}
      initial={{ opacity: 0, x: -18 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: Math.min(index * 0.07, 0.35), ease: [0.22, 1, 0.36, 1] }}
      className={`flex flex-wrap items-center gap-x-4 gap-y-2.5 sm:gap-5 rounded-2xl border px-4 py-4 sm:px-5 backdrop-blur-md transition-colors duration-300 ${
        isNext ? "border-amber-400/50 bg-slate-900/70" : "border-slate-800/80 bg-slate-900/40 hover:border-slate-700"
      }`}
    >
      <span className={`flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl border font-display leading-none ${
        isNext ? "border-amber-400/60 bg-amber-400/10 text-amber-400" : "border-slate-700 bg-[#0C1220] text-slate-200"
      }`}>
        <span className="text-xl">{day}</span>
        <span className="mt-0.5 text-[9px] tracking-[0.18em]">{month}</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          {CIRCUIT_STYLE[event.circuit] && (
            <span data-testid={`event-circuit-${event.id}`} className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[8px] font-black tracking-[0.12em] ${CIRCUIT_STYLE[event.circuit].cls}`}>
              {CIRCUIT_STYLE[event.circuit].label}
            </span>
          )}
          <span className="block text-sm sm:text-base font-bold leading-snug text-slate-50">{event.title}</span>
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3 w-3 text-amber-400/70" />
            {flagCode && (
              <img
                src={`https://flagcdn.com/w40/${flagCode}.png`}
                alt={country}
                loading="lazy"
                className="h-2.5 w-4 shrink-0 rounded-[2px] object-cover ring-1 ring-slate-700/60"
              />
            )}
            {event.location}
            <a
              href={mapsUrl(event.location)}
              target="_blank"
              rel="noreferrer"
              data-testid={`event-maps-${event.id}`}
              aria-label={`${t("news.openMaps")}: ${event.location}`}
              className="text-[9px] lowercase tracking-wide text-slate-600 underline decoration-slate-700 underline-offset-2 transition-colors duration-300 hover:text-sky-400 hover:decoration-sky-400/60"
            >
              maps
            </a>
          </span>
          <span>{range}{event.time ? ` · ${event.time}` : ""}</span>
        </span>
        {event.disciplines && (
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {event.disciplines.split(",").map((s) => s.trim()).filter(Boolean).map((disc) => (
              <span key={disc} className={`rounded-full border px-2 py-0.5 text-[8px] uppercase tracking-[0.16em] ${disciplineCls(disc)}`}>{disc}</span>
            ))}
            {/* su mobile il countdown sta in fila con le discipline */}
            {isNext && (
              <span className="sm:hidden">
                <CountdownChip days={daysUntil(event.date)} />
              </span>
            )}
          </span>
        )}
      </span>
      {isNext && (
        <span className="hidden sm:block sm:ml-auto">
          <CountdownChip days={daysUntil(event.date)} />
        </span>
      )}
    </motion.div>
  );
}

function TagChip({ tag }) {
  if (!tag) return null;
  const breaking = tag.toLowerCase().includes("breaking");
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] ${
      breaking
        ? "bg-red-500/15 border border-red-500/60 text-red-400"
        : "border border-amber-400/40 text-amber-400"
    }`}>
      {breaking && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-400" />}
      {tag}
    </span>
  );
}

function NewsCard({ item, locale, index, onZoom }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const img = item.image_url ? imgSrc(item.image_url, 640) : null;
  const date = new Date(item.date + "T00:00:00").toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  return (
    <motion.div
      data-testid={`news-card-${item.id}`}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: Math.min(index * 0.07, 0.35), ease: [0.22, 1, 0.36, 1] }}
      className={`overflow-hidden rounded-2xl border backdrop-blur-md transition-colors duration-300 ${open ? "border-sky-400/40 bg-slate-900/60" : "border-slate-800/80 bg-slate-900/40 hover:border-slate-700"}`}
    >
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="group flex w-full items-center gap-3.5 p-3.5 text-left focus:outline-none"
      >
        {img && (
          <span className="block h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-800">
            <img src={img} alt={item.title} loading="lazy" className="h-full w-full object-cover" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <TagChip tag={item.tag} />
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{date}</span>
          </span>
          <span className="mt-1 block text-sm sm:text-base font-bold text-slate-50 leading-snug">{item.title}</span>
          {item.body && <span className="mt-0.5 block text-xs text-slate-400 leading-relaxed line-clamp-2">{item.body}</span>}
        </span>
        <span className="flex shrink-0 flex-col items-center gap-1">
          <span className={`text-[9px] font-bold uppercase tracking-[0.16em] transition-colors ${open ? "text-sky-400" : "text-slate-500 group-hover:text-sky-400"}`}>
            {open ? t("news.showLess") : t("news.readMore")}
          </span>
          <motion.span
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className={`flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${open ? "border-sky-400/60 text-sky-400" : "border-slate-700 text-slate-400 group-hover:border-sky-400/50 group-hover:text-sky-400"}`}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </motion.span>
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-800/70 p-4" data-testid={`news-body-${item.id}`}>
              {img && (
                <button
                  type="button"
                  data-testid={`news-image-${item.id}`}
                  onClick={() => onZoom(imgSrc(item.image_url))}
                  aria-label={t("news.zoomImage")}
                  className="group/img mb-4 block w-full overflow-hidden rounded-xl border border-slate-800 bg-[#090D14] focus:outline-none"
                >
                  <img src={imgSrc(item.image_url, 960)} alt={item.title} loading="lazy" className="h-auto max-h-[65vh] w-full object-contain transition-transform duration-500 group-hover/img:scale-[1.02]" />
                </button>
              )}
              {item.body && <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{item.body}</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Tipi di disciplina di una gara: tecniche (SL/GS), veloci (DH/SG), combinata = entrambe
const raceDiscTypes = (e) => {
  if (!e.disciplines) return null;
  const types = new Set();
  for (const p of e.disciplines.split(",").map((s) => s.trim().toLowerCase())) {
    if (p.includes("discesa") || p.includes("super-g")) types.add("speed");
    else if (p.includes("combinata")) { types.add("tech"); types.add("speed"); }
    else types.add("tech");
  }
  return types;
};

export default function NewsAgenda() {
  const { t, locale } = useLang();
  const [events, setEvents] = useState(null);
  const [news, setNews] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(null);
  // filtri calendario: circuiti (WC/WCH/PAR) e tipo discipline (tecniche/veloci)
  const [filters, setFilters] = useState({ wc: true, wch: true, paralimpiadi: true, tech: true, speed: true });
  const toggleFilter = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));
  const mobile = useIsMobile();
  const [newsPeek, setNewsPeek] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
    api.get("/news").then((r) => setNews(r.data)).catch(() => setNews([]));
  }, []);

  if (events !== null && news !== null && events.length === 0 && news.length === 0) return null;

  const today = todayStr();
  const [seasonStart, seasonEnd] = seasonWindow();
  // solo gare della stagione corrente (luglio-giugno): Mondiali e Paralimpiadi future restano in archivio
  const inSeason = (e) => e.date >= seasonStart && e.date <= seasonEnd;
  const upcoming = (events || []).filter((e) => (e.date_end || e.date) >= today);
  const past = (events || [])
    .filter((e) => (e.date_end || e.date) < today)
    .sort((a, b) => (b.date_end || b.date).localeCompare(a.date_end || a.date));
  const upcomingSeasonRaces = upcoming.filter((e) => (e.kind || "gara") === "gara" && inSeason(e));
  const pastSeasonRaces = past.filter((e) => (e.kind || "gara") === "gara" && inSeason(e));
  const hasUpcoming = upcomingSeasonRaces.length > 0;
  // Senza gare future in stagione, riempi il pannello con quelle già disputate
  const races = hasUpcoming ? upcomingSeasonRaces : pastSeasonRaces;
  const sideEvents = upcoming.filter((e) => e.kind === "evento");
  const nextId = hasUpcoming && races.length ? races[0].id : null;
  const shownNews = (news || []).slice(0, 4);
  const visibleNews = !mobile || newsPeek ? shownNews : shownNews.slice(0, 2);
  // Bilancia le due colonne: tante gare quante ne servono a pareggiare le news (min 2)
  const initialRaces = Math.max(2, shownNews.length + 1 - sideEvents.length);
  const passesFilters = (e) => {
    if (e.circuit && !filters[e.circuit]) return false;
    const types = raceDiscTypes(e);
    if (types && !((types.has("tech") && filters.tech) || (types.has("speed") && filters.speed))) return false;
    return true;
  };
  const visibleEvents = (expanded ? races : races.slice(0, initialRaces)).filter(passesFilters);

  return (
    <section id="news" className="py-14 sm:py-28 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(56,189,248,0.05),transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("news.eyebrow")}</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50">
            {t("news.titleA")}<span className="text-amber-400">{t("news.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">{t("news.subtitle")}</p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
          <div>
            <Reveal delay={0.05}>
              <h3 className="font-display text-2xl sm:text-3xl text-slate-50 flex items-center gap-4">
                <span className="h-px w-10 bg-amber-400/70" />
                <CalendarDays className="h-5 w-5 text-amber-400" />
                {hasUpcoming ? t("news.agendaHeading") : t("news.pastHeading")}
              </h3>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-4 space-y-2.5" data-testid="agenda-legend">
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[9px] uppercase tracking-[0.14em] text-slate-500">
                  {[
                    { k: "wc", badge: "WC", badgeCls: "border-sky-400/60 bg-sky-400/10 text-sky-300", label: t("news.circuitWc") },
                    { k: "wch", badge: "WCH", badgeCls: "border-amber-400/70 bg-amber-400/10 text-amber-400", label: t("news.circuitWch") },
                    { k: "paralimpiadi", badge: "PAR", badgeCls: "border-emerald-400/70 bg-emerald-400/10 text-emerald-400", label: t("news.circuitPar") },
                  ].map((f) => (
                    <button
                      key={f.k}
                      type="button"
                      data-testid={`agenda-filter-${f.k}`}
                      onClick={() => toggleFilter(f.k)}
                      aria-pressed={filters[f.k]}
                      className={`flex items-center gap-1.5 transition-opacity duration-300 hover:opacity-100 ${filters[f.k] ? "" : "opacity-35"}`}
                    >
                      <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-[4px] border ${filters[f.k] ? "border-sky-400/70 bg-sky-400/20 text-sky-300" : "border-slate-600 text-transparent"}`}>
                        <Check className="h-2.5 w-2.5" />
                      </span>
                      <span className={`rounded border px-1.5 py-0.5 font-black ${f.badgeCls}`}>{f.badge}</span>
                      {f.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[9px] uppercase tracking-[0.14em] text-slate-500">
                  {[
                    { k: "tech", dot: "bg-indigo-400", label: `SL · GS ${t("news.legendTech")}` },
                    { k: "speed", dot: "bg-orange-400", label: `DH · SG ${t("news.legendSpeed")}` },
                  ].map((f) => (
                    <button
                      key={f.k}
                      type="button"
                      data-testid={`agenda-filter-${f.k}`}
                      onClick={() => toggleFilter(f.k)}
                      aria-pressed={filters[f.k]}
                      className={`flex items-center gap-1.5 transition-opacity duration-300 hover:opacity-100 ${filters[f.k] ? "" : "opacity-35"}`}
                    >
                      <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-[4px] border ${filters[f.k] ? "border-sky-400/70 bg-sky-400/20 text-sky-300" : "border-slate-600 text-transparent"}`}>
                        <Check className="h-2.5 w-2.5" />
                      </span>
                      <span className={`h-2 w-2 rounded-full ${f.dot}`} />
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
            <div className="mt-5 space-y-3" data-testid="agenda-list">
              {events === null && <p className="text-sm text-slate-500">{t("gallery.loading")}</p>}
              {events !== null && races.length === 0 && (
                <div data-testid="agenda-empty" className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 p-6 text-sm text-slate-400 leading-relaxed">
                  {t("news.emptyAgenda")}
                </div>
              )}
              {events !== null && races.length > 0 && visibleEvents.length === 0 && (
                <div data-testid="agenda-no-filter-results" className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 p-6 text-sm text-slate-400 leading-relaxed">
                  {t("news.noFilterResults")}
                </div>
              )}
              {visibleEvents.map((e, i) => (
                <EventRow key={e.id} event={e} isNext={e.id === nextId} locale={locale} index={i} />
              ))}
              {races.length > initialRaces && (
                <button
                  data-testid="agenda-expand-btn"
                  onClick={() => setExpanded(!expanded)}
                  aria-expanded={expanded}
                  className="group mx-auto mt-1 flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 transition-colors duration-300 hover:border-amber-400/60 hover:text-amber-400"
                >
                  {expanded ? t("news.showLess") : t("news.showAll", { n: races.length - initialRaces })}
                  <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </motion.span>
                </button>
              )}
            </div>

            <div className="mt-8" data-testid="side-events-block">
              <Reveal delay={0.05}>
                <h4 className="font-display text-xl sm:text-2xl text-slate-50 flex items-center gap-3">
                  <span className="h-px w-8 bg-sky-400/70" />
                  {t("news.eventsHeading")}
                </h4>
              </Reveal>
              {sideEvents.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {sideEvents.map((e, i) => (
                    <EventRow key={e.id} event={e} isNext={false} locale={locale} index={i} />
                  ))}
                </div>
              ) : (
                <p data-testid="side-events-empty" className="mt-4 rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 px-5 py-4 text-xs text-slate-500">
                  {t("news.noEvents")}
                </p>
              )}
            </div>
          </div>

          <div>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display text-2xl sm:text-3xl text-slate-50 flex items-center gap-4">
                  <span className="h-px w-10 bg-sky-400/70" />
                  <Newspaper className="h-5 w-5 text-sky-400" />
                  {t("news.newsHeading")}
                </h3>
                <Link
                  to="/archivio-stampa"
                  data-testid="news-archive-btn"
                  className="group inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 transition-colors duration-300 hover:border-sky-400/60 hover:text-sky-300"
                >
                  {t("news.archiveCta")}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
            <div className="mt-5 space-y-2.5" data-testid="news-list">
              {news === null && <p className="text-sm text-slate-500">{t("gallery.loading")}</p>}
              {news !== null && shownNews.length === 0 && (
                <p data-testid="news-empty" className="text-sm text-slate-500">{t("news.emptyNews")}</p>
              )}
              {visibleNews.map((n, i) => (
                <NewsCard key={n.id} item={n} locale={locale} index={i} onZoom={setZoom} />
              ))}
            </div>
            {mobile && shownNews.length > 2 && (
              <ExpandPill open={newsPeek} onToggle={() => setNewsPeek(!newsPeek)} hiddenCount={shownNews.length - 2} testid="expand-news" />
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {zoom && (
          <motion.div
            data-testid="news-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoom(null)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#090D14]/95 backdrop-blur-md p-4"
          >
            <button className="absolute top-5 right-5 text-slate-300 hover:text-amber-400" aria-label={t("news.close")}>
              <X className="h-8 w-8" />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={zoom}
              alt=""
              className="max-h-[85vh] max-w-full rounded-xl border border-slate-700 object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
